import { useCallback, useMemo, useRef } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  ConvertProformaToVatMutation,
  FakturowniaUnmatchedCountDocument,
  StudentDetailDocument,
  StudentProformasDocument,
  StudentProformasQuery,
  useConvertProformaToVatMutation,
  usePinProformaToContributionMutation,
  useStudentProformasQuery,
  useUnpinProformaFromContributionMutation,
} from 'core/graphql';
import {
  documentMayExist,
  fakturowniaReasonKey,
} from 'core/utils/invoiceIssueReasons';

import { useToasts } from '../Toast/useToasts';
import { evictInvoiceIssueLists, evictInvoiceLists } from './evictFinanceLists';
import { isPermissionDeniedError } from './errors';

/** One proforma of the student (`StudentProformas`). */
export type IStudentProforma = NonNullable<
  NonNullable<StudentProformasQuery['fakturowniaInvoices']>[number]
>;

/** The document `issueVatFromProforma` returns (the proforma on a dry run). */
export type IProformaVatDocument = NonNullable<
  NonNullable<ConvertProformaToVatMutation['issueVatFromProforma']>['invoice']
>;

/** Normalized outcome of a dry run or a real «Выставить VAT». */
export interface IProformaVatResult {
  ok: boolean;
  errorCode: string | null;
  errors: string[];
  /**
   * Dry run → the proforma; real call → the new VAT; `VAT_ALREADY_ISSUED` →
   * the VAT that already exists.
   */
  invoice: IProformaVatDocument | null;
  /** A real fiscal document may exist despite `ok === false`. */
  documentAtRisk: boolean;
}

export interface IUseProformasApi {
  proformas: IStudentProforma[];
  loading: boolean;
  error?: ApolloError;
  permissionDenied: boolean;
  /** Pin a proforma to a взнос. Toasts on its own; `true` on success. */
  link: (invoiceId: string, contributionId: string) => Promise<boolean>;
  /** Drop the pin. Toasts on its own; `true` on success. */
  unlink: (invoiceId: string) => Promise<boolean>;
  linking: boolean;
  /** Phase 1 of «Выставить VAT»: every server check, nothing written. */
  checkVat: (invoiceId: string) => Promise<IProformaVatResult>;
  /** Phase 2: creates a REAL VAT document. Guarded against double-submit. */
  issueVat: (invoiceId: string) => Promise<IProformaVatResult>;
  checkingVat: boolean;
  issuingVat: boolean;
}

const NO_PROFORMAS: IStudentProforma[] = [];

const cleanErrors = (errors?: Array<string | null> | null): string[] =>
  (errors || []).filter((e): e is string => Boolean(e));

/**
 * ТЗ 0101 — proformas of one student: the list, pin / unpin to a взнос and
 * the two-phase «Выставить VAT».
 *
 * `studentId` MUST be the relay global id of the student (`student.id`) —
 * the `StudentDetail` refetch is keyed by it (see `useIssueFaktura`).
 */
export const useProformas = (
  studentId: string,
  { skip = false }: { skip?: boolean } = {},
): IUseProformasApi => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const { data, loading, error } = useStudentProformasQuery({
    variables: { studentId },
    skip: skip || !studentId,
    fetchPolicy: 'cache-and-network',
  });

  const refetchQueries = useMemo(
    () => [
      { query: FakturowniaUnmatchedCountDocument },
      { query: StudentDetailDocument, variables: { id: studentId } },
      { query: StudentProformasDocument, variables: { studentId } },
    ],
    [studentId],
  );

  const [pinMutation, pinState] = usePinProformaToContributionMutation({
    refetchQueries,
    awaitRefetchQueries: true,
    update: evictInvoiceLists,
  });
  const [unpinMutation, unpinState] = useUnpinProformaFromContributionMutation({
    refetchQueries,
    awaitRefetchQueries: true,
    update: evictInvoiceLists,
  });
  const [dryRunMutation, dryRunState] = useConvertProformaToVatMutation();
  const [vatMutation, vatState] = useConvertProformaToVatMutation({
    refetchQueries,
    awaitRefetchQueries: true,
    update: evictInvoiceIssueLists,
  });

  const reasonText = useCallback(
    (code: string | null | undefined, errors: string[]) =>
      code
        ? t(fakturowniaReasonKey(code), { defaultValue: code })
        : errors.join('\n') || undefined,
    [t],
  );

  const toast = useCallback(
    (ok: boolean, title: string, text?: string) =>
      addToast({
        data: { type: ok ? ToastTypes.success : ToastTypes.error, title, text },
        type: GlobalToastTypeNames.FakturowniaMatch,
      }),
    [addToast],
  );

  const link = useCallback(
    async (invoiceId: string, contributionId: string) => {
      try {
        const res = await pinMutation({
          variables: { invoiceId, contributionId },
        });
        const payload = res.data?.linkProformaToContribution;
        if (payload?.success) {
          toast(true, t('pages.studentDetail.proformas.toasts.linked'));
          return true;
        }
        toast(
          false,
          t('pages.studentDetail.proformas.toasts.linkFailed'),
          reasonText(payload?.errorCode, cleanErrors(payload?.errors)),
        );
        return false;
      } catch (e) {
        toast(
          false,
          t('pages.studentDetail.proformas.toasts.linkFailed'),
          (e as Error).message,
        );
        return false;
      }
    },
    [pinMutation, toast, reasonText, t],
  );

  const unlink = useCallback(
    async (invoiceId: string) => {
      try {
        const res = await unpinMutation({ variables: { invoiceId } });
        const payload = res.data?.unlinkProformaFromContribution;
        if (payload?.success) {
          toast(true, t('pages.studentDetail.proformas.toasts.unlinked'));
          return true;
        }
        toast(
          false,
          t('pages.studentDetail.proformas.toasts.unlinkFailed'),
          reasonText(payload?.errorCode, cleanErrors(payload?.errors)),
        );
        return false;
      } catch (e) {
        toast(
          false,
          t('pages.studentDetail.proformas.toasts.unlinkFailed'),
          (e as Error).message,
        );
        return false;
      }
    },
    [unpinMutation, toast, reasonText, t],
  );

  const checkVat = useCallback(
    async (invoiceId: string): Promise<IProformaVatResult> => {
      try {
        const res = await dryRunMutation({
          variables: { invoiceId, dryRun: true },
        });
        const payload = res.data?.issueVatFromProforma;
        return {
          ok: Boolean(payload?.success),
          errorCode: payload?.success ? null : payload?.errorCode || null,
          errors: cleanErrors(payload?.errors),
          invoice: payload?.invoice || null,
          // A dry run writes nothing — nothing can be at risk.
          documentAtRisk: false,
        };
      } catch (e) {
        return {
          ok: false,
          errorCode: null,
          errors: [(e as ApolloError).message],
          invoice: null,
          documentAtRisk: false,
        };
      }
    },
    [dryRunMutation],
  );

  // Synchronous double-submit guard: the vendor has no idempotency key on our
  // side of the wire, a second click in the same tick must not POST again.
  const inFlight = useRef(false);

  const issueVat = useCallback(
    async (invoiceId: string): Promise<IProformaVatResult> => {
      if (inFlight.current) {
        return {
          ok: false,
          errorCode: 'ISSUE_IN_FLIGHT',
          errors: [],
          invoice: null,
          documentAtRisk: false,
        };
      }
      inFlight.current = true;
      try {
        const res = await vatMutation({
          variables: { invoiceId, dryRun: false },
        });
        const payload = res.data?.issueVatFromProforma;
        const ok = Boolean(payload?.success);
        return {
          ok,
          errorCode: ok ? null : payload?.errorCode || null,
          errors: cleanErrors(payload?.errors),
          invoice: payload?.invoice || null,
          documentAtRisk: ok
            ? false
            : documentMayExist(payload?.errorCode, payload?.documentCreated),
        };
      } catch (e) {
        // A transport failure on OUR side says nothing about whether the
        // server's POST landed — treat it as possibly created.
        return {
          ok: false,
          errorCode: 'FAKTUROWNIA_AMBIGUOUS',
          errors: [(e as ApolloError).message],
          invoice: null,
          documentAtRisk: true,
        };
      } finally {
        inFlight.current = false;
      }
    },
    [vatMutation],
  );

  const fresh = useMemo(
    () =>
      data?.fakturowniaInvoices
        ? data.fakturowniaInvoices.filter((p): p is IStudentProforma =>
            Boolean(p),
          )
        : null,
    [data],
  );

  // Перепроверка M3: `update` мутаций выбрасывает `fakturowniaInvoices` из
  // кэша (evictInvoiceLists), и до ответа рефетча `data` пуст. Блок
  // «Проформы» на пустом списке возвращал null — и размонтировал вместе с
  // собой окно «Выставить VAT» ровно в момент, когда оно должно было
  // показать номер нового VAT или «документ мог создаться». Пока список
  // перечитывается, отдаём последний показанный (того же студента).
  const lastShown = useRef<{ studentId: string; list: IStudentProforma[] }>({
    studentId,
    list: NO_PROFORMAS,
  });
  if (fresh) {
    lastShown.current = {
      studentId,
      list: fresh.length ? fresh : NO_PROFORMAS,
    };
  }
  let proformas: IStudentProforma[] = NO_PROFORMAS;
  if (fresh) proformas = fresh.length ? fresh : NO_PROFORMAS;
  else if (loading && lastShown.current.studentId === studentId) {
    proformas = lastShown.current.list;
  }

  return {
    proformas,
    loading,
    error,
    permissionDenied: isPermissionDeniedError(error),
    link,
    unlink,
    linking: pinState.loading || unpinState.loading,
    checkVat,
    issueVat,
    checkingVat: dryRunState.loading,
    issuingVat: vatState.loading,
  };
};
